import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Link2, Scale, Search } from "lucide-react";

import { apiFetch } from "../../context/AppContext";

function questionKey(question) {
  return `${question.module || "gap"}:${question.id}`;
}

function itemSection(item) {
  const category = String(item?.category || "").trim();
  const code = String(item?.code || "").trim();
  if (category && category.toLowerCase() !== "general") return category;
  if (code) return code;
  return "";
}

function itemRef(item) {
  const regulation = String(item?.regulation || "").trim();
  if (regulation) return regulation;
  const code = String(item?.code || "").trim();
  const section = itemSection(item);
  if (code && code !== section) return code;
  return "";
}

const selectClass = "h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

function sectionNamesFor(groups, countryName) {
  const names = [];
  groups.forEach((group) => {
    const matches = countryName ? group.matches.filter((match) => match.country === countryName) : group.matches;
    if (matches.length && group.section && !names.includes(group.section)) names.push(group.section);
  });
  return names;
}

function matchesByCountry(matches) {
  const buckets = [];
  matches.forEach((match) => {
    let bucket = buckets.find((item) => item.country === match.country);
    if (!bucket) {
      bucket = { country: match.country, lawName: match.lawName || "", items: [] };
      buckets.push(bucket);
    }
    if (!bucket.lawName && match.lawName) bucket.lawName = match.lawName;
    bucket.items.push(match);
  });
  return buckets;
}

function CountryChips({ buckets }) {
  const shown = buckets.slice(0, 2);
  const extra = buckets.length - shown.length;
  return (
    <span className="flex shrink-0 items-center gap-1">
      {shown.map((bucket) => (
        <span
          key={bucket.country}
          className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600"
        >
          {bucket.country}
        </span>
      ))}
      {extra > 0 && <span className="text-[11px] font-bold text-slate-400">+{extra}</span>}
    </span>
  );
}

function QuestionDropdown({ group, open, showCountries }) {
  const buckets = matchesByCountry(group.matches);
  const body = (
    <div className="divide-y divide-slate-100 border-t border-slate-100 bg-slate-50/70">
      {buckets.map((bucket) => (
        <div key={bucket.country} className="px-4 py-2.5 sm:pl-14">
          <p className="text-xs font-bold text-slate-800">
            {bucket.country}
            {bucket.lawName && <span className="ml-2 font-medium text-slate-400">{bucket.lawName}</span>}
          </p>
          <ul className="mt-1 space-y-0.5">
            {bucket.items.map((match) => (
              <li key={match.itemId} className="text-sm leading-5 text-slate-700">
                {match.ref && <span className="mr-2 font-semibold text-slate-500">{match.ref}</span>}
                {match.title}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  const heading = (
    <>
      <span className="mt-0.5 shrink-0 rounded-md bg-sky-50 px-1.5 py-0.5 text-[11px] font-bold text-sky-700 ring-1 ring-sky-100">
        {group.ref || group.id}
      </span>
      <p className="min-w-0 flex-1 text-sm font-semibold leading-5 text-slate-900 line-clamp-2" title={group.title}>{group.title}</p>
      {showCountries && <CountryChips buckets={buckets} />}
    </>
  );

  if (open) {
    return (
      <article className="border-b border-slate-100 bg-white last:border-b-0">
        <div className="flex items-start gap-3 px-4 py-3">{heading}</div>
        {body}
      </article>
    );
  }

  return (
    <details className="group border-b border-slate-100 bg-white last:border-b-0">
      <summary className="flex cursor-pointer list-none items-start gap-3 px-4 py-3 transition hover:bg-slate-50 [&::-webkit-details-marker]:hidden">
        {heading}
        <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-slate-400 transition group-open:rotate-180" />
      </summary>
      {body}
    </details>
  );
}

export default function LawMappingLinksPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groups, setGroups] = useState([]);
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState(() => searchParams.get("country") || "");
  const [section, setSection] = useState("");
  const [question, setQuestion] = useState("");
  const [query, setQuery] = useState("");
  const [openSection, setOpenSection] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const listRes = await apiFetch("/tenant/compliance-checklists");
        const listData = await listRes.json().catch(() => ({}));
        if (!listRes.ok) throw new Error(listData.detail || "Unable to load checklists");

        const sources = (listData.checklists || []).filter((entry) => entry.item_count || entry.law_mapped);
        const bankRes = await apiFetch("/tenant/compliance-checklists/question-bank");
        const bankData = await bankRes.json().catch(() => ({}));
        if (!bankRes.ok) throw new Error(bankData.detail || "Unable to load DPDP questions");

        const bank = (bankData.modules || [])
          .flatMap((module) => module.questions || [])
          .filter((item) => item.module === "gap");
        const bankByKey = new Map(bank.map((item) => [questionKey(item), item]));
        const bankOrder = new Map(bank.map((item, index) => [questionKey(item), index]));

        const loaded = await Promise.all(sources.map(async (entry) => {
          const params = `region=${encodeURIComponent(entry.region)}&country=${encodeURIComponent(entry.country)}`;
          const [itemRes, mapRes] = await Promise.all([
            apiFetch(`/tenant/compliance-checklists/items?${params}`),
            apiFetch(`/tenant/compliance-checklists/law-mapping?${params}`),
          ]);
          const itemData = await itemRes.json().catch(() => ({}));
          const mapData = await mapRes.json().catch(() => ({}));
          if (!mapRes.ok) throw new Error(mapData.detail || `Unable to load ${entry.country}`);
          return {
            region: entry.region,
            country: entry.country,
            lawName: mapData.law_name || "",
            items: itemRes.ok ? (itemData.items || []) : [],
            links: mapData.links || [],
          };
        }));

        const byQuestion = new Map();
        const countryNames = [];
        loaded.forEach((entry) => {
          const itemsById = new Map(entry.items.map((item) => [item.id, item]));
          let linked = false;
          (entry.links || []).forEach((link) => {
            const item = itemsById.get(link.checklist_item_id) || {};
            const match = {
              country: entry.country,
              region: entry.region,
              lawName: entry.lawName,
              itemId: link.checklist_item_id,
              title: item.title || link.checklist_item_id,
              ref: itemRef(item),
            };
            (link.questions || []).filter((itemQuestion) => itemQuestion.module === "gap").forEach((itemQuestion) => {
              const key = questionKey(itemQuestion);
              const known = bankByKey.get(key);
              if (!byQuestion.has(key)) {
                byQuestion.set(key, {
                  key,
                  id: itemQuestion.id,
                  title: known?.title || itemQuestion.title || itemQuestion.id,
                  ref: known?.ref || itemQuestion.ref || "",
                  section: known?.section || itemQuestion.section || link.section || "General",
                  order: bankOrder.has(key) ? bankOrder.get(key) : 100000,
                  matches: [],
                });
              }
              const group = byQuestion.get(key);
              const seen = group.matches.some((row) => row.country === match.country && row.itemId === match.itemId);
              if (!seen) group.matches.push(match);
              linked = true;
            });
          });
          if (linked && !countryNames.includes(entry.country)) countryNames.push(entry.country);
        });

        const nextGroups = [...byQuestion.values()].map((group) => ({
          ...group,
          matches: [...group.matches].sort((left, right) => (
            left.country.localeCompare(right.country) || left.title.localeCompare(right.title)
          )),
        }));
        nextGroups.sort((left, right) => left.order - right.order || left.title.localeCompare(right.title));

        if (cancelled) return;
        const knownCountries = countryNames.sort((left, right) => left.localeCompare(right));
        const requestedCountry = searchParams.get("country") || "";
        const activeCountry = knownCountries.includes(requestedCountry) ? requestedCountry : "";
        setGroups(nextGroups);
        setCountries(knownCountries);
        setCountry(activeCountry);
        setSection(sectionNamesFor(nextGroups, activeCountry)[0] || "");
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || "Unable to load linked questions");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        matches: country ? group.matches.filter((match) => match.country === country) : group.matches,
      }))
      .filter((group) => group.matches.length > 0)
      .filter((group) => !section || group.section === section)
      .filter((group) => {
        if (!text) return true;
        const blob = [
          group.ref,
          group.id,
          group.title,
          group.section,
          ...group.matches.flatMap((match) => [match.country, match.ref, match.title, match.lawName]),
        ].join(" ").toLowerCase();
        return blob.includes(text);
      });
  }, [groups, country, section, query]);

  const sections = useMemo(() => sectionNamesFor(groups, country), [groups, country]);

  const activeQuestion = filtered.some((group) => group.key === question) ? question : "";
  const visible = activeQuestion ? filtered.filter((group) => group.key === activeQuestion) : filtered;
  const sectionNames = [];
  visible.forEach((group) => {
    if (!sectionNames.includes(group.section)) sectionNames.push(group.section);
  });

  const browsingAll = !section;
  const activeBrowse = openSection && sectionNames.includes(openSection) ? openSection : sectionNames[0];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/tenant/compliance-checklists")}
            title="Back to checklists"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
            <Link2 className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900">Linked laws</h1>
            <p className="mt-1 text-sm text-slate-500">
              {country
                ? `Requirements matched for ${country}.`
                : "Country requirements matched to each DPDP question."}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/tenant/compliance-checklists/map")}
          className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm hover:border-blue-200 hover:text-blue-700"
        >
          <Scale className="h-4 w-4" />
          Edit mapping
        </button>
      </div>

      {loading ? (
        <p className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
          Loading question matches…
        </p>
      ) : error ? (
        <p className="rounded-2xl border border-red-100 bg-white px-6 py-16 text-center text-sm text-red-600">{error}</p>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-700">No question matches yet</p>
          <p className="mt-1 text-sm text-slate-500">Map a country law to DPDP, then the matches will show here.</p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200 bg-white p-3">
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-400">Search</span>
              <span className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search"
                  className={`${selectClass} pl-9`}
                />
              </span>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-400">Country</span>
              <select
                value={country}
                onChange={(event) => {
                  const nextCountry = event.target.value;
                  const names = sectionNamesFor(groups, nextCountry);
                  const nextParams = new URLSearchParams(searchParams);
                  if (nextCountry) nextParams.set("country", nextCountry);
                  else nextParams.delete("country");
                  setCountry(nextCountry);
                  setSection(names[0] || "");
                  setQuestion("");
                  setOpenSection("");
                  setSearchParams(nextParams, { replace: true });
                }}
                className={selectClass}
              >
                <option value="">All countries</option>
                {countries.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-400">Section</span>
              <select
                value={sections.includes(section) ? section : ""}
                onChange={(event) => {
                  setSection(event.target.value);
                  setQuestion("");
                  setOpenSection("");
                }}
                className={selectClass}
              >
                <option value="">All sections</option>
                {sections.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-400">Question</span>
              <select
                value={activeQuestion}
                onChange={(event) => setQuestion(event.target.value)}
                className={selectClass}
              >
                <option value="">All questions</option>
                {filtered.map((group) => (
                  <option key={group.key} value={group.key}>
                    {group.ref ? `${group.ref} · ` : ""}{group.title}
                  </option>
                ))}
              </select>
            </label>
          </div>
          </div>

          {visible.length === 0 ? (
            <p className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
              No question matches for that filter.
            </p>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <h2 className="text-sm font-bold text-slate-900">{section || "All sections"}</h2>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                  {visible.length} {visible.length === 1 ? "question" : "questions"}
                </span>
              </div>
              <div className="max-h-[calc(100dvh-17rem)] overflow-y-auto">
                {browsingAll ? sectionNames.map((name) => {
                  const rows = visible.filter((group) => group.section === name);
                  const expanded = name === activeBrowse;
                  return (
                    <section key={name}>
                      <button
                        type="button"
                        onClick={() => setOpenSection(name)}
                        className="flex w-full items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-left text-sm font-bold text-slate-700 hover:bg-slate-100"
                      >
                        <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition ${expanded ? "" : "-rotate-90"}`} />
                        <span className="min-w-0 flex-1 truncate">{name}</span>
                        <span className="text-[11px] font-bold text-slate-400">{rows.length}</span>
                      </button>
                      {expanded && rows.map((group) => (
                        <QuestionDropdown key={group.key} group={group} open={Boolean(activeQuestion)} showCountries={!country} />
                      ))}
                    </section>
                  );
                }) : visible.map((group) => (
                  <QuestionDropdown key={group.key} group={group} open={Boolean(activeQuestion)} showCountries={!country} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
